import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { exportInactiveStores } from "../../services";
import {
  INACTIVE_STORE_EXPORT_FORMAT,
  INACTIVE_STORE_STATE,
  InactiveStoreListQuery,
  InactiveStoreResponse,
} from "../../types/inactive-stores.type";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { SURFACE } from "../common/designTokens";
import ExportButton from "../ExportButton";
import { DaysCell, LastSaleCell, MapLinkCell } from "./StoreCells";
import StorePagination from "./StorePagination";
import {
  EMPTY_TEXT,
  LIST_LOAD_ERROR_TEXT,
  MISSING,
  SEARCH_DEBOUNCE_MS,
  StoreFilterValues,
  exportFallbackFilename,
  storeCountLabel,
  toFilterParams,
} from "./inactiveStores";
import { useInactiveStorePage } from "./useInactiveStorePage";

const TABLE_SX = { "& td, & th": { whiteSpace: "nowrap" } } as const;
const HEAD_SX = { "& th": { bgcolor: SURFACE.head, fontWeight: 600 } } as const;
const NAME_CELL_SX = { whiteSpace: "normal !important", minWidth: 200 } as const;

/** The FIN Excel's main sheet, in its order. */
const COLUMNS: { header: string; align?: "right" | "center" }[] = [
  { header: "CODE" },
  { header: "สาขา" },
  { header: "van" },
  { header: "ชื่อร้าน" },
  { header: "GROUP" },
  { header: "ขายล่าสุด" },
  { header: "จำนวนวัน", align: "right" },
  { header: "พิกัด", align: "center" },
];

interface StoreTableProps {
  days: number;
  filters: StoreFilterValues;
  refDate: string;
  lookbackDays: number;
}

/**
 * Every active shop past the threshold that the filters let through, longest without a bill
 * first, a page at a time. The export sends the very same filters, so the file holds every
 * row of this list rather than the page on screen.
 */
export default function StoreTable({
  days,
  filters,
  refDate,
  lookbackDays,
}: Readonly<StoreTableProps>) {
  const { branchCode, van, shopGroup } = filters;
  const search = useDebounce(filters.search.trim(), SEARCH_DEBOUNCE_MS);

  const filterParams = useMemo(
    () => toFilterParams(days, { branchCode, van, shopGroup, search }),
    [days, branchCode, van, shopGroup, search],
  );
  const listQuery = useMemo<InactiveStoreListQuery>(
    () => ({ ...filterParams, state: INACTIVE_STORE_STATE.active }),
    [filterParams],
  );
  const { rows, count, page, setPage, loading, hasError } = useInactiveStorePage(listQuery);

  const requestCsvExport = () =>
    exportInactiveStores({ ...filterParams, format: INACTIVE_STORE_EXPORT_FORMAT.csv });
  const requestXlsxExport = () =>
    exportInactiveStores({ ...filterParams, format: INACTIVE_STORE_EXPORT_FORMAT.xlsx });
  const isExportDisabled = loading || count === 0;
  const isEmpty = !loading && rows.length === 0;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        mb={1}
      >
        <Typography variant="body2" fontWeight={600}>
          พบ {storeCountLabel(count)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <ExportButton
            label="Export CSV"
            request={requestCsvExport}
            fallbackFilename={exportFallbackFilename(
              days,
              refDate,
              INACTIVE_STORE_EXPORT_FORMAT.csv,
            )}
            disabled={isExportDisabled}
          />
          <ExportButton
            label="Export XLSX"
            request={requestXlsxExport}
            fallbackFilename={exportFallbackFilename(
              days,
              refDate,
              INACTIVE_STORE_EXPORT_FORMAT.xlsx,
            )}
            disabled={isExportDisabled}
          />
        </Stack>
      </Stack>

      {hasError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {LIST_LOAD_ERROR_TEXT}
        </Alert>
      )}

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={TABLE_SX}>
          <TableHead sx={HEAD_SX}>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.header} align={column.align}>
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">
                  <Box py={3}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {EMPTY_TEXT}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((store) => (
                <StoreRow key={store.customer_uuid} store={store} lookbackDays={lookbackDays} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <StorePagination count={count} page={page} onPageChange={setPage} />
    </>
  );
}

function StoreRow({
  store,
  lookbackDays,
}: Readonly<{ store: InactiveStoreResponse; lookbackDays: number }>) {
  return (
    <TableRow hover>
      <TableCell>{store.customer_code}</TableCell>
      <TableCell>{store.branch_code}</TableCell>
      <TableCell>{store.van}</TableCell>
      <TableCell sx={NAME_CELL_SX}>{store.customer_name ?? MISSING}</TableCell>
      <TableCell>{store.shop_group}</TableCell>
      <LastSaleCell date={store.last_sale_date} />
      <DaysCell days={store.days_since_sale} lookbackDays={lookbackDays} />
      <MapLinkCell latitude={store.latitude} longitude={store.longitude} />
    </TableRow>
  );
}
